import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { RovoIcon } from '@atlaskit/logo';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import type { ConnectorPromoConfig } from '../connector-promo-config-types.ts';
import { useAdminHubConnectorLink } from '../hooks/useAdminHubConnectorLink.ts';
import { normalizeFields } from '../utils/analytics.ts';

import { ConnectorPromoCardBase } from './ConnectorPromoCardBase.tsx';

const messages = defineMessages({
	heading: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.nthPromo.heading',
		defaultMessage: 'Your team’s search results could be better',
		description: 'Heading for the connector promo card shown after Nth search',
	},
	body: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.nthPromo.body',
		defaultMessage: 'Connect your team’s most used apps so everyone can search in one place.',
		description: 'Body text for the connector promo card shown after Nth search',
	},
	setupLabel: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.nthPromo.setupLabel',
		defaultMessage: 'Set up',
		description: 'Primary CTA button label on the Nth promo card',
	},
});

type ConnectorNthPromoCardProps = Pick<ConnectorPromoConfig, 'recommendedConnectors'> & {
	onDismissPromo: () => void;
	testId?: string;
};

export const ConnectorNthPromoCard = ({
	recommendedConnectors,
	onDismissPromo,
	testId = 'connector-nth-promo-card',
}: ConnectorNthPromoCardProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createEvent } = useConfluenceAnalyticsEvents();
	const adminHubLink = useAdminHubConnectorLink();

	const handlePrimaryClick = useCallback(() => {
		createEvent({
			type: 'sendUIEvent',
			data: {
				source: 'ConnectorNthPromoCard',
				action: 'clicked',
				actionSubject: 'button',
				attributes: normalizeFields({
					connectorKey: recommendedConnectors[0]?.key,
					container: 'ConnectorPromoNavSearch',
					testId: `${testId}--primary-cta`,
				}),
			},
		}).fire();
	}, [recommendedConnectors, createEvent, testId]);

	const handleDismiss = useCallback(() => {
		createEvent({
			type: 'sendUIEvent',
			data: {
				source: 'ConnectorNthPromoCard',
				action: 'clicked',
				actionSubject: 'button',
				attributes: normalizeFields({
					connectorKey: recommendedConnectors[0]?.key,
					container: 'ConnectorPromoNavSearch',
					testId: `${testId}--dismiss`,
				}),
			},
		}).fire();
		onDismissPromo();
	}, [recommendedConnectors, onDismissPromo, createEvent, testId]);

	if (recommendedConnectors.length === 0) {
		return null;
	}

	return (
		<ConnectorPromoCardBase
			icon={<RovoIcon size="small" shouldUseHexLogo />}
			heading={formatMessage(messages.heading)}
			body={formatMessage(messages.body)}
			primaryCtaLabel={formatMessage(messages.setupLabel)}
			onPrimaryClick={handlePrimaryClick}
			primaryCtaLink={adminHubLink}
			onDismiss={handleDismiss}
			testId={testId}
		/>
	);
};
